import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Users, Heart, TrendingUp } from 'lucide-react';

const About = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: <Target className="w-12 h-12 text-[#D4AF37]" />,
      title: 'Our Mission',
      description: 'To empower healthcare providers with innovative technology that streamlines referrals, strengthens professional relationships, and ultimately improves patient outcomes.'
    },
    {
      icon: <Users className="w-12 h-12 text-[#D4AF37]" />,
      title: 'Collaboration First',
      description: 'We believe in the power of seamless collaboration between healthcare professionals, ensuring every patient receives the right care at the right time.'
    },
    {
      icon: <Heart className="w-12 h-12 text-[#D4AF37]" />,
      title: 'Patient-Centered',
      description: 'Every feature we build is designed with patient care in mind, making healthcare more accessible, efficient, and personalized.'
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-[#D4AF37]" />,
      title: 'Continuous Innovation',
      description: 'We are committed to staying ahead of healthcare technology trends, constantly evolving our platform to meet changing needs.'
    },
  ];

  const team = [
    {
      name: 'Dr. William Gray',
      role: 'Founder & CEO',
      bio: 'Board-certified oral surgeon with 15+ years of experience in dental practice management.',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=William'
    },
    {
      name: 'Sarah Johnson',
      role: 'Chief Technology Officer',
      bio: 'Former healthcare IT leader with expertise in HIPAA-compliant software development.',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah'
    },
    {
      name: 'Michael Chen',
      role: 'Head of Product',
      bio: 'Product strategist focused on user experience and healthcare workflow optimization.',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Michael'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1628] to-[#2d3e50] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">About ReferBridge</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We're on a mission to transform how healthcare professionals collaborate and deliver patient care through innovative referral management technology.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0A1628] mb-6 text-center">
            Our Story
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              ReferBridge was born from firsthand experience with the challenges of managing patient referrals in a busy dental practice. Our founder, Dr. William Gray, witnessed daily how inefficient referral processes led to delayed care, frustrated colleagues, and missed opportunities for collaboration.
            </p>
            <p className="mb-4">
              In 2024, we set out to build a better solution—one that would bring together specialists, referring providers, and patients on a single, intuitive platform. What started as a simple referral tracking tool has evolved into a comprehensive network management system used by thousands of healthcare professionals nationwide.
            </p>
            <p>
              Today, ReferBridge is proud to facilitate over 5 million patient encounters, connecting more than 35,000 providers across 1,150 medical facilities. Our commitment to innovation, security, and user experience drives everything we do.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0A1628] mb-12 text-center">
            Our Values
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="bg-white shadow-lg border-none">
                <CardContent className="p-8">
                  <div className="mb-4">{value.icon}</div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0A1628] mb-12 text-center">
            Meet Our Leadership Team
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <Card key={index} className="bg-white shadow-lg border-none text-center">
                <CardContent className="p-8">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-32 h-32 rounded-full mx-auto mb-4 border-4 border-[#D4AF37]"
                  />
                  <h3 className="text-xl font-bold text-[#0A1628] mb-1">
                    {member.name}
                  </h3>
                  <p className="text-[#D4AF37] font-semibold mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600">
                    {member.bio}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] to-[#1a2332]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">
            Our Impact
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D4AF37] mb-2">5M+</div>
              <p className="text-gray-300 text-lg">Patient Encounters</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D4AF37] mb-2">35K+</div>
              <p className="text-gray-300 text-lg">Healthcare Providers</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D4AF37] mb-2">1,150+</div>
              <p className="text-gray-300 text-lg">Medical Facilities</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D4AF37] mb-2">98%</div>
              <p className="text-gray-300 text-lg">Customer Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#0A1628] mb-6">
            Join Thousands of Healthcare Professionals
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Experience the future of referral management. Start your free trial today.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B] text-lg px-8"
          >
            Get Started Free
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
